import React from "react";
import { Drawer, SelectPicker } from "rsuite";
import { Button, FormGroup } from "reactstrap";
import PropTypes from "prop-types";
import RoomsRateplans from "../RoomsRateplans";
import InfoIcon from "../../../assets/img/blue-info-icon.svg";
import GreenInfoIcon from "../../../assets/img/green-info.svg";
import { useRoomsRateplansDrawerHandlers } from "../hooks/useRoomsRateplansDrawerHandlers";
import {
  ROOMS_RATEPLANS_EDIT_INSTRUCTION,
  ROOMS_RATEPLANS_VIEW_INSTRUCTION,
} from "./offerTypeConstants";

/**
 * RoomsRateplansDrawer - Drawer for selecting rooms and rate plans
 * Contains hotel selection dropdown, action buttons, and RoomsRateplans grid
 */
// Wraps the grid in a disabled fieldset when read-only, and in nothing at all
// otherwise — see the call site.
const ReadOnlyGridWrapper = ({ readOnly, children }) =>
  readOnly ? (
    <fieldset disabled className="rooms-rateplans-readonly">
      {children}
    </fieldset>
  ) : (
    children
  );

ReadOnlyGridWrapper.propTypes = {
  readOnly: PropTypes.bool,
  children: PropTypes.node,
};

function RoomsRateplansDrawer({
  readOnly = false,
  // Drawer state
  open,
  onClose,
  // Properties
  selectedProperties = [],
  selectedHotelInDrawer = null,
  setSelectedHotelInDrawer,
  filteredPropertyOptions = [],
  properties = [],
  selectedProperty = null,
  hotelSelectContainerRef = null,
  // Rate plans and rooms
  drawerRatePlans = [],
  setDrawerRatePlans,
  drawerRooms = [],
  setDrawerRooms,
  drawerDataPropertyId = null,
  setDrawerDataPropertyId = undefined,
  drawerSelections = [],
  setDrawerSelections,
  // Promo data
  promo = null,
  promoData = null,
  onFieldChange,
  isEdit = false,
  // Success message
  successMessage = "",
  setSuccessMessage,
  // API functions
  getRatePlansByProperty,
  getRoomsByProperty,
  // Helpers
  buildAllRoomRateplanCombos,
  savedSelectionsBeforeDrawerRef = null,
  roomsRateplansByPropertyRef = null,
}) {
  const {
    handleHotelChange,
    handleDeselectAll,
    handleSelectAll,
    handleSave,
    handleConfirm,
  } = useRoomsRateplansDrawerHandlers({
    properties,
    propertyOptions: filteredPropertyOptions,
    selectedHotelInDrawer,
    setSelectedHotelInDrawer,
    selectedProperty,
    setDrawerRatePlans,
    setDrawerRooms,
    setDrawerDataPropertyId,
    drawerSelections,
    promo,
    onFieldChange,
    selectedProperties,
    setSuccessMessage,
    savedSelectionsBeforeDrawerRef,
    onClose,
    getRatePlansByProperty,
    getRoomsByProperty,
    buildAllRoomRateplanCombos,
    roomsRateplansByPropertyRef,
  });

  return (
    <Drawer
      placement="right"
      open={open}
      onClose={onClose}
      size="lg"
      className="rooms-rateplans-drawer"
    >
      <Drawer.Header>
        <Drawer.Title>Rooms and Rate Plans</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <p className="drawer-instruction-text">
          {readOnly
            ? ROOMS_RATEPLANS_VIEW_INSTRUCTION
            : ROOMS_RATEPLANS_EDIT_INSTRUCTION}
        </p>
        <hr className="drawer-divider" />

        {/* Success Banner or Info Banner - only show if multiple properties selected and not in edit mode */}
        {!isEdit && selectedProperties.length > 1 && (
          <>
            {successMessage ? (
              <div className="drawer-success-banner">
                <img src={GreenInfoIcon} alt="success" className="info-icon" />
                <span className="info-text">{successMessage}</span>
              </div>
            ) : (
              <div className="drawer-info-banner">
                <img src={InfoIcon} alt="info" className="info-icon" />
                <span className="info-text">
                  You can update Rooms and Rate Plan selections for each hotel
                  separately.
                </span>
              </div>
            )}
          </>
        )}

        {/* Hotel Selection and Action Buttons */}
        <div className="drawer-hotel-actions-row">
          {/* Hotel Selection - only show if multiple properties selected and not in edit mode */}
          {!isEdit && selectedProperties.length > 1 && (
            <div className="drawer-hotel-selection">
              <FormGroup>
                <div
                  className="position-relative"
                  ref={hotelSelectContainerRef}
                >
                  <SelectPicker
                    id="hotel-select"
                    value={selectedHotelInDrawer}
                    data={filteredPropertyOptions}
                    placeholder="Select hotel"
                    block
                    cleanable={false}
                    searchable={false}
                    container={() => hotelSelectContainerRef.current}
                    onChange={handleHotelChange}
                  />
                </div>
              </FormGroup>
            </div>
          )}

          {/* Action Buttons - for both single and multiple property selections;
              a read-only view has nothing to select. */}
          {!readOnly && (
            <div className="drawer-actions-header">
              <Button color="secondary" size="sm" onClick={handleDeselectAll}>
                Deselect All
              </Button>
              <Button color="secondary" size="sm" onClick={handleSelectAll}>
                Select All
              </Button>
            </div>
          )}
        </div>
        {/* Read-only only: the disabled fieldset stops the room-type buttons (and
            the keyboard), and its class stops clicks on the tick cells. Rendered ONLY
            when read-only — a bare <fieldset> carries UA border/padding/min-width and
            would reshape the editable drawer (and CampaignActionForm's, which never
            passes readOnly). */}
        <ReadOnlyGridWrapper readOnly={readOnly}>
          <RoomsRateplans
            promo={promoData}
            ratePlans={drawerRatePlans}
            rooms={drawerRooms}
            dataPropertyId={drawerDataPropertyId}
            onSelectionChange={(selections) => setDrawerSelections(selections)}
            onSave={
              !readOnly && !isEdit && selectedProperties.length > 1
                ? handleSave
                : undefined
            }
          />
        </ReadOnlyGridWrapper>

        {/* Footer Buttons at bottom of drawer body */}
        <div className="drawer-footer-content">
          <Button
            appearance="subtle"
            onClick={onClose}
            className="cancel-btn-footer"
          >
            Close
          </Button>
          {!readOnly && (
            <Button
              appearance="primary"
              onClick={handleConfirm}
              className="confirm-selections-btn-footer"
              disabled={!drawerSelections || drawerSelections.length === 0}
            >
              Confirm Selections
            </Button>
          )}
        </div>
      </Drawer.Body>
    </Drawer>
  );
}

RoomsRateplansDrawer.propTypes = {
  readOnly: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedProperties: PropTypes.arrayOf(PropTypes.string),
  selectedHotelInDrawer: PropTypes.string,
  setSelectedHotelInDrawer: PropTypes.func.isRequired,
  filteredPropertyOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  properties: PropTypes.array,
  selectedProperty: PropTypes.shape({
    propertyId: PropTypes.string,
    propertyName: PropTypes.string,
  }),
  hotelSelectContainerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  drawerRatePlans: PropTypes.array,
  setDrawerRatePlans: PropTypes.func.isRequired,
  drawerRooms: PropTypes.array,
  setDrawerRooms: PropTypes.func.isRequired,
  drawerDataPropertyId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  setDrawerDataPropertyId: PropTypes.func,
  drawerSelections: PropTypes.array,
  setDrawerSelections: PropTypes.func.isRequired,
  promo: PropTypes.object,
  promoData: PropTypes.object,
  onFieldChange: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  successMessage: PropTypes.string,
  setSuccessMessage: PropTypes.func.isRequired,
  getRatePlansByProperty: PropTypes.func.isRequired,
  getRoomsByProperty: PropTypes.func.isRequired,
  buildAllRoomRateplanCombos: PropTypes.func.isRequired,
  savedSelectionsBeforeDrawerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  roomsRateplansByPropertyRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};

export default RoomsRateplansDrawer;
